import React, {useEffect, useMemo} from 'react';

import {RootLayer} from '@sail/ui';

import {Observability} from '@stripe-internal/conversations-client';
import {RequestedAction} from '@stripe-internal/support-conversations-controller';

import {AppConfig, AppParentMessageBus, AppProfile} from '../types';
import {AppContext} from '../contexts/AppContext';
import {AppImpl} from '../App';
import {ClientFetch, defaultClientFetch} from '../lib/util/clientFetch';
import {ConversationsUIProviders} from './ConversationsUIProviders';

type Props = {
  config: AppConfig;
  profile: AppProfile;
  clientFetch?: ClientFetch;
  handleRequestedAction?: (action: RequestedAction) => void;
  parentMessageBus?: AppParentMessageBus;
  observability?: Partial<Observability>;
  children: React.ReactNode;
};

export function AppProvider({
  config,
  profile,
  clientFetch = defaultClientFetch(),
  handleRequestedAction,
  parentMessageBus,
  observability,
  children,
}: Props) {
  const app = useMemo(() => {
    return new AppImpl(
      config,
      profile,
      clientFetch,
      handleRequestedAction,
      parentMessageBus,
      observability,
    );
  }, [
    config,
    profile,
    clientFetch,
    handleRequestedAction,
    parentMessageBus,
    observability,
  ]);

  useEffect(() => {
    app.subscribeToParentMessageBus();
    return () => {
      app.unsubscribeFromParentMessageBus();
    };
  }, [app]);

  return (
    <AppContext.Provider value={app}>
      <RootLayer>
        <ConversationsUIProviders>{children}</ConversationsUIProviders>
      </RootLayer>
    </AppContext.Provider>
  );
}
